// The admin's branding (ADR 0048), from ``/config/public``: the app
// name, primary colour, login text and logo. ``null`` fields mean the
// built-in Sheafworks defaults.
//
// The colour replaces the green of the palette (index.css). The server
// only accepts colours with at least 4.5:1 contrast against white, so
// it is used as is in light mode; dark mode gets a lighter shade of the
// same hue, raised until it reaches 4.5:1 against the dark background.

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { PublicBranding } from "@/auth/flows";
import { usePublicConfig } from "@/lib/publicConfig";

const NONE: PublicBranding = {
  app_name: null,
  primary_color: null,
  login_text: null,
  logo_url: null,
};

export function useBranding(): PublicBranding {
  return usePublicConfig().data?.branding ?? NONE;
}

/** The admin's name for the app, else "Sheafworks". */
export function useAppName(): string {
  const { t } = useTranslation();
  return useBranding().app_name ?? t("app.name");
}

type Hsl = [number, number, number];

export function hexToHsl(hex: string): Hsl {
  const [r, g, b] = [1, 3, 5].map(
    (i) => parseInt(hex.slice(i, i + 2), 16) / 255,
  ) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, Math.round(l * 100)];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [Math.round(h * 60), Math.round(s * 100), Math.round(l * 100)];
}

function hslToRgb([h, s, l]: Hsl): [number, number, number] {
  const sl = s / 100;
  const ll = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sl * Math.min(ll, 1 - ll);
  const f = (n: number) =>
    ll - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return [f(0), f(8), f(4)];
}

function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  ) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: Hsl, b: Hsl): number {
  const [la, lb] = [luminance(hslToRgb(a)), luminance(hslToRgb(b))];
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const DARK_BACKGROUND: Hsl = [216, 32, 9];

/** The shade used in dark mode: same hue, light enough to read. */
export function darkShade(color: Hsl): Hsl {
  const [h, s] = color;
  let l = Math.max(color[2], 45);
  while (l < 90 && contrast([h, s, l], DARK_BACKGROUND) < 4.5) l += 1;
  return [h, s, l];
}

const css = ([h, s, l]: Hsl) => `${h} ${s}% ${l}%`;

/** CSS overriding the palette's primary colour, or "" for the default.
 *
 * Doubled selectors so it wins over index.css wherever the bundler
 * puts that stylesheet. */
export function brandCss(hex: string | null): string {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return "";
  const light = hexToHsl(hex);
  const [h, s, l] = light;
  const dark = darkShade(light);
  return (
    `:root:root{--primary:${css(light)};--ring:${css(light)};` +
    `--accent:${h} 26% 94%;--accent-foreground:${css([h, s, Math.max(l - 6, 0)])}}` +
    `:root.dark{--primary:${css(dark)};--ring:${css(dark)};` +
    `--accent:${h} 22% 17%;--accent-foreground:${h} 35% 78%}`
  );
}

/** Applies the colour and the page title; mounted once at the root. */
export function BrandingEffect() {
  const { primary_color } = useBranding();
  const name = useAppName();
  useEffect(() => {
    document.title = name;
  }, [name]);
  useEffect(() => {
    const id = "sheafworks-brand";
    let style = document.getElementById(id);
    const text = brandCss(primary_color);
    if (!text) {
      style?.remove();
      return;
    }
    if (!style) {
      style = document.createElement("style");
      style.id = id;
      document.head.appendChild(style);
    }
    style.textContent = text;
  }, [primary_color]);
  return null;
}
